import React from 'react'
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Grid,
} from '@mui/material'
import { fontSizes, fontWeights } from '../../../Common/Typography/TypographyStyles'
import { useAppSelector } from '../../../../redux/store'
import { selectFilteredTaskComplianceMetrics } from '../../../../redux/task/manageTasks/selector'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import VerifiedIcon from '@mui/icons-material/Verified'

// ─── Metric Card ─────────────────────────────────────────────────────────────

interface MetricCardProps {
  label: string
  value: string | number
  subLabel?: string
  background: string
  Icon: React.ElementType
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, subLabel, background, Icon }) => (
  <Card
    sx={{
      background,
      color: 'white',
      borderRadius: '12px',
      width: '100%',
    }}
    elevation={2}
  >
    <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={1}>
        <Typography
          sx={{
            fontSize: fontSizes.size14,
            fontWeight: fontWeights[500],
            textShadow: '1px 1px 3px rgba(0,0,0,0.4)',
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
        <Icon sx={{ fontSize: '36px', opacity: 0.9 }} />
      </Box>
      <Typography
        sx={{
          fontSize: fontSizes.size36,
          fontWeight: fontWeights[700],
          textShadow: '1px 1px 3px rgba(0,0,0,0.4)',
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      {subLabel && (
        <Typography sx={{ fontSize: fontSizes.size13, opacity: 0.85, mt: 0.75 }}>
          {subLabel}
        </Typography>
      )}
    </CardContent>
  </Card>
)

// ─── Main Component ──────────────────────────────────────────────────────────

const TaskReportChart: React.FC = () => {
  const overallMetrics = useAppSelector(selectFilteredTaskComplianceMetrics)

  return (
    <Card elevation={0}>
      <CardHeader
        title={
          <Typography
            sx={{
              fontSize: fontSizes.size16,
              fontWeight: fontWeights[700],
            }}
          >
            Task Compliance Report
          </Typography>
        }
      />

      {/* ── Metric Cards ─────────────────────────────────────────────── */}
      <Box sx={{ px: 2, pb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <MetricCard
              label='Overall Compliance Rate'
              value={`${overallMetrics.overallComplianceRate.toFixed(2)}%`}
              subLabel='All completed tasks'
              background='linear-gradient(135deg, #1CB5E0 0%, #000851 100%)'
              Icon={VerifiedIcon}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <MetricCard
              label='On-Time Completion Rate'
              value={`${overallMetrics.onTimeDeliveryRate.toFixed(2)}%`}
              subLabel='All completed tasks that met their due date'
              background='linear-gradient(135deg, #9ebd13 0%, #008552 100%)'
              Icon={AccessTimeIcon}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <MetricCard
              label='Overdue Rate'
              value={`${overallMetrics.overdueRate.toFixed(2)}%`}
              subLabel='All tasks where the due date has passed'
              background='linear-gradient(135deg, #d53369 0%, #cbad6d 100%)'
              Icon={WarningAmberIcon}
            />
          </Grid>
        </Grid>
      </Box>
    </Card>
  )
}

export default TaskReportChart
